// src/components/onboarding/Step_BlacklistAlliance.jsx
// Rendered only when tenants.include_dnc === true. Collects the tenant's BA
// API key and SAN, writes them to tenants, and stamps blacklist_alliance_enabled_at.
// The API key + SAN are write-only from the UI — never read back into client state.

import { useState } from 'react';
import { Shield, Key, FileCheck, AlertCircle } from 'lucide-react';
import supabase from '../../lib/supabaseClient';

export default function Step_BlacklistAlliance({ onNext }) {
  const [apiKey, setApiKey] = useState('');
  const [san, setSan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!apiKey.trim() || !san.trim()) {
      setError('Both fields are required.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Authentication required');

      const { data: profile, error: profileError } = await supabase
        .from('users_profile')
        .select('tenant_id')
        .eq('id', user.id)
        .single();
      if (profileError || !profile) throw new Error('User profile not found');

      const { error: updateError } = await supabase
        .from('tenants')
        .update({
          blacklist_alliance_api_key: apiKey.trim(),
          blacklist_alliance_san: san.trim(),
          blacklist_alliance_enabled_at: new Date().toISOString(),
        })
        .eq('id', profile.tenant_id);

      if (updateError) throw updateError;

      onNext();
    } catch (err) {
      console.error('BA onboarding error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield size={32} className="text-white" />
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
          Connect your Blacklist Alliance account
        </h2>
        <p className="text-gray-600 mt-2 max-w-xl mx-auto">
          We'll use these credentials to scrub your bulk uploads against the National DNC and litigator list before they reach your CRM.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          Your SAN proves you hold a valid National DNC subscription per FCC/FTC rules. Both values are stored encrypted at rest and never sent back to the browser.
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Key className="w-4 h-4 text-amber-600" />
            Blacklist Alliance API Key
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Paste your BA API key"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            autoComplete="off"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-amber-600" />
            Subscription Account Number (SAN)
          </label>
          <input
            type="text"
            value={san}
            onChange={(e) => setSan(e.target.value)}
            placeholder="e.g. ABCDEF1234"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            autoComplete="off"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={loading || !apiKey.trim() || !san.trim()}
          className={`px-8 py-3 rounded-xl font-medium transition-all flex items-center gap-2
            ${(apiKey.trim() && san.trim()) && !loading
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-lg transform hover:scale-105'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        >
          {loading ? 'Saving...' : 'Connect & Continue'}
        </button>
      </div>
    </div>
  );
}
